import { useEffect, useState } from 'react'

/**
 * **Eine Medienabfrage als React-Zustand** ([Z-16](../../../../docs/phasen/zusatz-16-pwa-mein-tag.md)).
 *
 * Bis Z-16 kam jede Anpassung ans Telefon mit CSS-Varianten aus (`lg:`, `sm:`, `pointer:
 * coarse`) – beide Fassungen standen im DOM, und der Browser blendete eine aus. Für „Mein Tag"
 * auf dem Telefon reicht das nicht mehr: dort ist nicht *dieselbe* Ansicht enger gesetzt,
 * sondern eine **andere Zusammenstellung** derselben Zonen. Beide gleichzeitig zu rendern
 * hieße, jede Abfrage, jede Mutation und jeden Test doppelt zu haben – und ein `queryByText`
 * fände jede Überschrift zweimal.
 *
 * Deshalb entscheidet **JavaScript**, welche Fassung gerendert wird – mit derselben Grenze wie
 * das CSS ([E-240](../../../../docs/ENTSCHEIDUNGEN.md#e-240)), damit Seitenleiste und Inhalt
 * nie zwei verschiedene Meinungen haben, was „schmal" ist.
 *
 * **Ohne `matchMedia` ist die Antwort `false`.** Das ist der Testfall (happy-dom rechnet mit
 * 1024 px) und jeder Rahmen, der keine Breite kennt: dann läuft der Rechner-Zweig, und alle
 * Bestandstests sehen, was sie immer sahen.
 */
export function useMedienabfrage(abfrage: string): boolean {
  const [trifft, setTrifft] = useState(() => liest(abfrage))

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const liste = window.matchMedia(abfrage)
    const beiWechsel = (e: MediaQueryListEvent) => setTrifft(e.matches)
    /** Der Anfangswert ist beim ersten Rendern gelesen; hier nur nachziehen, falls er sprang. */
    setTrifft(liste.matches)
    liste.addEventListener('change', beiWechsel)
    return () => liste.removeEventListener('change', beiWechsel)
  }, [abfrage])

  return trifft
}

function liest(abfrage: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(abfrage).matches
}

/**
 * **Dieselbe Grenze wie `lg:` in Tailwind** – 1024 px, siehe E-240. Unterhalb ist die
 * Seitenleiste eine Schublade, und „Mein Tag" zeigt seine Telefon-Zusammenstellung.
 * `1023.98` statt `1023`, damit ein Fenster mit gebrochener Breite (Zoom) nicht zwischen
 * beide Regeln fällt – dieselbe Zahl, die Tailwind für `max-lg:` erzeugt.
 */
export const TELEFON_ABFRAGE = '(max-width: 1023.98px)'

export function useTelefonLayout(): boolean {
  return useMedienabfrage(TELEFON_ABFRAGE)
}
